import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { COMPANY, PLANS_PUBLIC } from "../lib/companyInfo";
import Footer from "../components/Footer";

const today = () =>
  new Intl.DateTimeFormat("tr-TR", { year: "numeric", month: "long", day: "2-digit" }).format(new Date());

function LegalShell({ title, testid, children }) {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid={testid}>
      <div className="max-w-3xl w-full mx-auto px-4 md:px-8 py-10 md:py-16 flex-1">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="text-sm text-stone-600 hover:text-orange-700 inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Ana sayfa
          </Link>
          <button
            onClick={() => window.print()}
            className="text-xs text-stone-500 hover:text-orange-700 inline-flex items-center gap-1.5 no-print"
          >
            <Printer className="w-4 h-4" /> Yazdır
          </button>
        </div>
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-2">Yasal Bilgi</div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mb-2">{title}</h1>
        <div className="text-xs text-stone-500 mb-8">
          Yürürlük tarihi: {today()} · Son güncelleme: {today()}
        </div>
        <article className="prose prose-stone max-w-none text-sm text-stone-700 leading-relaxed space-y-4">
          {children}
        </article>
      </div>
      <Footer />
    </div>
  );
}

function H2({ children }) {
  return <h2 className="font-display font-bold text-slate-900 text-lg mt-8 mb-2">{children}</h2>;
}

function P({ children }) {
  return <p className="text-stone-700">{children}</p>;
}

function CompanyBlock() {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 text-xs text-stone-700 space-y-1">
      <div className="font-semibold text-slate-900">Satıcı (Hizmet Sağlayıcı)</div>
      <div>Unvan: {COMPANY.name}</div>
      <div>Vergi Kimlik No: {COMPANY.vkn}</div>
      <div>Adres: {COMPANY.address}</div>
      <div>Telefon: {COMPANY.phone}</div>
      <div>E-posta: {COMPANY.email}</div>
      <div>Web: {COMPANY.website}</div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Mesafeli Satış Sözleşmesi
 * --------------------------------------------------------------------- */
export function DistanceSale() {
  return (
    <LegalShell title="Mesafeli Satış Sözleşmesi" testid="legal-msale-page">
      <CompanyBlock />

      <H2>1. Taraflar</H2>
      <P>
        İşbu Mesafeli Satış Sözleşmesi ("Sözleşme"), yukarıda unvan ve iletişim
        bilgileri belirtilen <b>{COMPANY.name}</b> ("SATICI") ile ödeme sırasında
        bilgileri kaydedilen abone kullanıcı ("ALICI") arasında elektronik ortamda
        kurulmuştur. Sözleşme, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve
        Mesafeli Sözleşmeler Yönetmeliği hükümlerine tabidir.
      </P>

      <H2>2. Sözleşmenin Konusu</H2>
      <P>
        SATICI'nın işlettiği "{COMPANY.brand}" adlı web tabanlı SaaS yazılım
        hizmetinin ("Hizmet") ALICI tarafından aylık abonelik modeliyle satın
        alınması, ücret, ifa şekli, cayma hakkı ve tarafların hak ve yükümlülüklerine
        ilişkin esasların düzenlenmesidir.
      </P>

      <H2>3. Hizmet, Fiyat ve Ödeme</H2>
      <P>SATICI'nın sunduğu abonelik paketleri:</P>
      <ul className="list-disc pl-5 space-y-1">
        {PLANS_PUBLIC.map((p) => (
          <li key={p.key}>
            <b>{p.name}</b>: Aylık {p.price} ₺ (KDV dahil) — {p.limit}
          </li>
        ))}
      </ul>
      <P>
        Ödemeler kredi kartı ile Iyzico altyapısı üzerinden alınır. Abonelik bedeli,
        seçilen pakete göre satın alma anında peşin olarak tahsil edilir. Fatura,
        e-posta ile ALICI'ya iletilir.
      </P>

      <H2>4. Sözleşmenin Kurulması ve İfa</H2>
      <P>
        ALICI ödeme sayfasında Sözleşme'yi okuyup onayladıktan sonra "Satın Al"
        butonuna basmakla Sözleşme'yi kabul etmiş sayılır. Ödemenin başarıyla
        tamamlanmasının ardından abonelik SATICI tarafından derhal, elektronik
        ortamda ifa edilir.
      </P>

      <H2>5. Cayma Hakkı</H2>
      <P>
        Mesafeli Sözleşmeler Yönetmeliği'nin 15/1-(ğ) maddesi uyarınca,
        elektronik ortamda anında ifa edilen hizmetlere ilişkin sözleşmelerde
        <b> cayma hakkı bulunmamaktadır</b>. Bu nedenle abonelik satın alma
        işleminden sonra ALICI cayma hakkını kullanamaz. Ancak ALICI, mevcut
        abonelik döneminin sonunda aboneliğini iptal ederek yenilenmesini
        durdurabilir. Ayrıntı için <Link to="/iptal-iade" className="underline">İptal ve İade Koşulları</Link>'nı inceleyiniz.
      </P>

      <H2>6. ALICI'nın Beyan ve Taahhütleri</H2>
      <P>
        ALICI, siparişten önce Hizmet'in temel niteliklerini, satış fiyatını, ödeme
        ve ifa şeklini okuyup bilgi sahibi olduğunu ve elektronik ortamda gerekli
        teyidi verdiğini kabul eder. Verilen bilgilerin doğruluğundan ALICI
        sorumludur.
      </P>

      <H2>7. Uyuşmazlıkların Çözümü</H2>
      <P>
        İşbu Sözleşme'den doğabilecek uyuşmazlıklarda, Gümrük ve Ticaret Bakanlığı'nca
        her yıl açıklanan parasal sınırlar dahilinde Tüketici Hakem Heyetleri ve
        Tüketici Mahkemeleri yetkilidir.
      </P>

      <H2>8. Yürürlük</H2>
      <P>
        {PLANS_PUBLIC.length} maddeden oluşan işbu Sözleşme, ALICI tarafından
        elektronik ortamda okunup kabul edildikten ve ödeme tamamlandıktan sonra
        yürürlüğe girer.
      </P>
    </LegalShell>
  );
}

/* -----------------------------------------------------------------------
 * İptal ve İade Koşulları
 * --------------------------------------------------------------------- */
export function RefundPolicy() {
  return (
    <LegalShell title="İptal ve İade Koşulları" testid="legal-refund-page">
      <H2>1. Genel</H2>
      <P>
        "{COMPANY.brand}" hizmeti aylık dönemlerle sunulan bir dijital SaaS
        hizmettir. Ödemesi yapılan aylık dönem içerisinde hizmet kesintisiz
        sunulur.
      </P>

      <H2>2. Cayma Hakkı</H2>
      <P>
        Mesafeli Sözleşmeler Yönetmeliği m. 15/1-(ğ) uyarınca elektronik ortamda
        anında ifa edilen dijital hizmet aboneliklerinde cayma hakkı bulunmaz.
        Bu nedenle satın alma sonrası aylık ücret için iade yapılmaz.
      </P>

      <H2>3. Aboneliğin İptali</H2>
      <P>
        ALICI, kendi hesabından "Abonelik" ekranı üzerinden dilediği zaman
        aboneliğini iptal edebilir; iptal etmesi halinde aboneliği mevcut ödeme
        döneminin sonuna kadar aktif kalır ve otomatik yenileme durur. Sonraki
        ay ücret tahsil edilmez.
      </P>

      <H2>4. İstisnai İade Halleri</H2>
      <ul className="list-disc pl-5 space-y-1">
        <li>Mükerrer tahsilat: aynı fatura için birden fazla ödeme alınması halinde fazla tahsilat 10 iş günü içinde iade edilir.</li>
        <li>SATICI kaynaklı hizmet kesintisi 48 saati aşarsa, ilgili ay ücretinden kesinti süresi oranında iade yapılır.</li>
        <li>Yanlış hesaba tahsilat: kanıt sunulması halinde ücret 10 iş günü içinde iade edilir.</li>
      </ul>

      <H2>5. İade Yöntemi ve Süresi</H2>
      <P>
        İadeler, ödemenin yapıldığı kredi kartına Iyzico üzerinden yapılır. Kartın
        bulunduğu bankanın yansıma süresine bağlı olarak iade işlemi 7-14 iş günü
        içerisinde tamamlanır.
      </P>

      <H2>6. Başvuru</H2>
      <P>
        İade veya iptal talepleri <a href={`mailto:${COMPANY.email}`} className="underline">{COMPANY.email}</a>
        adresine iletilmelidir. Talebe fatura numarası, tahsilat tarihi ve kısa
        açıklama eklenmelidir.
      </P>
    </LegalShell>
  );
}

/* -----------------------------------------------------------------------
 * Gizlilik ve Güvenlik Politikası + KVKK
 * --------------------------------------------------------------------- */
export function Privacy() {
  return (
    <LegalShell title="Gizlilik ve Güvenlik Politikası" testid="legal-privacy-page">
      <CompanyBlock />

      <H2>1. Amaç</H2>
      <P>
        Bu politika, "{COMPANY.brand}" platformunun ("Platform") kullanıcılarına
        ait kişisel verilerin 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK")
        çerçevesinde nasıl işlendiği, saklandığı ve korunduğunu açıklar.
      </P>

      <H2>2. Veri Sorumlusu</H2>
      <P>
        Veri sorumlusu <b>{COMPANY.name}</b>'dır ({COMPANY.address}).
      </P>

      <H2>3. Toplanan Kişisel Veriler</H2>
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Kimlik & iletişim</b>: ad soyad, e-posta, telefon, galeri adı.</li>
        <li><b>Ödeme</b>: ödeme yöntemi tercihi, fatura bilgileri. Kart bilgileri Iyzico'da işlenir; Platform tarafında saklanmaz.</li>
        <li><b>Kullanım verileri</b>: giriş logları, IP, tarayıcı ve cihaz bilgileri.</li>
        <li><b>Galeri operasyonu verileri</b>: sisteme girdiğiniz araç, kiralama, müşteri (ad, telefon, TC) ve gider bilgileri.</li>
      </ul>

      <H2>4. İşleme Amaçları ve Hukuki Sebep</H2>
      <P>
        Kişisel veriler; hizmetin sunulması, faturalandırma, müşteri destek, güvenlik
        ve yasal yükümlülüklerin yerine getirilmesi amacıyla, KVKK m.5/2-(c)
        (sözleşmenin kurulması ve ifası) ve m.5/2-(ç) (hukuki yükümlülük) hukuki
        sebeplerine dayanılarak işlenmektedir.
      </P>

      <H2>5. Aktarım</H2>
      <P>
        Verileriniz; ödeme altyapısı sağlayıcısı Iyzico, e-fatura sağlayıcıları,
        bulut altyapı sağlayıcıları ve yasal olarak zorunlu kamu kurumları ile
        gerektiği ölçüde paylaşılabilir. Yurt dışına aktarım yapılmamaktadır.
      </P>

      <H2>6. Saklama Süresi</H2>
      <P>
        Kullanıcı hesabı aktif kaldığı sürece ve müteakip 10 yıl boyunca (VUK
        yükümlülüğü nedeniyle) veriler saklanır. Sonrasında silinir veya
        anonimleştirilir.
      </P>

      <H2>7. Güvenlik</H2>
      <P>
        Veriler HTTPS/TLS ile şifreli iletilir. Şifreler bcrypt ile
        hash'lenerek saklanır. Ödeme bilgileri PCI-DSS uyumlu Iyzico altyapısında
        işlenir. Yetkisiz erişimi önlemek için rol tabanlı yetkilendirme uygulanır.
      </P>

      <H2>8. Haklarınız (KVKK m.11)</H2>
      <ul className="list-disc pl-5 space-y-1">
        <li>İşlenen kişisel verileri öğrenme, işleme amacını ve kullanımını bilme.</li>
        <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme.</li>
        <li>KVKK m.7'de öngörülen şartlarla silinmesini/yok edilmesini isteme.</li>
        <li>Otomatik sistemler yoluyla aleyhinize sonuç doğuran işlemlere itiraz etme.</li>
        <li>Zarara uğramanız halinde tazminat talep etme.</li>
      </ul>
      <P>
        Bu haklarınızı kullanmak için başvurularınızı{" "}
        <a href={`mailto:${COMPANY.email}`} className="underline">{COMPANY.email}</a>
        {" "}adresine iletebilirsiniz.
      </P>

      <H2>9. Çerezler</H2>
      <P>
        Platform, oturum yönetimi ve güvenlik amacıyla zorunlu çerezleri kullanır.
        Pazarlama veya üçüncü taraf takip çerezleri kullanılmaz.
      </P>
    </LegalShell>
  );
}

/* -----------------------------------------------------------------------
 * Kullanıcı Sözleşmesi
 * --------------------------------------------------------------------- */
export function UserAgreement() {
  return (
    <LegalShell title="Kullanıcı Sözleşmesi" testid="legal-tos-page">
      <CompanyBlock />

      <H2>1. Taraflar ve Konu</H2>
      <P>
        Bu sözleşme, "{COMPANY.brand}" hizmetini ("Hizmet") sunan {COMPANY.name}
        ile Hizmeti kullanan kişi/kurum ("Kullanıcı") arasında akdedilmiştir.
      </P>

      <H2>2. Hesap ve Erişim</H2>
      <P>
        Kullanıcı, hesabına ait e-posta ve şifrenin gizliliğinden bizzat
        sorumludur. Yönetici, kendi galerisi altında çalışanlar için yeni
        hesaplar oluşturabilir ve şifrelerini sıfırlayabilir.
      </P>

      <H2>3. Kabul Edilebilir Kullanım</H2>
      <ul className="list-disc pl-5 space-y-1">
        <li>Yasa dışı içerik yüklenemez.</li>
        <li>Başkalarının haklarını ihlal edecek şekilde kullanılamaz.</li>
        <li>Otomatik betiklerle veya kötü niyetli erişimle Platform bütünlüğü bozulamaz.</li>
        <li>Hizmet üçüncü şahıslara devredilemez, alt lisanslanamaz.</li>
      </ul>

      <H2>4. Fikri Mülkiyet</H2>
      <P>
        Platformun tüm arayüz, yazılım, marka ve içeriklerinin tüm hakları
        {" "}{COMPANY.name}'a aittir. Kullanıcı, yalnızca abonelik kapsamında
        kullanma lisansına sahiptir.
      </P>

      <H2>5. Sorumluluk Sınırı</H2>
      <P>
        Hizmet "olduğu gibi" sunulur. Öngörülebilir dolaylı zararlar için
        sorumluluk, ilgili ayda ödenen abonelik bedeliyle sınırlıdır.
      </P>

      <H2>6. Değişiklikler</H2>
      <P>
        {COMPANY.name}, Hizmet özelliklerini ve bu sözleşmeyi güncelleyebilir;
        güncellemeler Platformda yayımlandığı anda yürürlüğe girer.
      </P>

      <H2>7. Fesih</H2>
      <P>
        Kullanıcı dilediği zaman aboneliğini iptal edebilir. Sözleşmenin ihlali
        halinde {COMPANY.name} hesabı askıya alma veya kapatma hakkını saklı tutar.
      </P>

      <H2>8. Uygulanacak Hukuk</H2>
      <P>
        İşbu Sözleşme Türk Hukuku'na tabidir. Uyuşmazlıklarda Mersin Mahkemeleri
        ve İcra Daireleri yetkilidir.
      </P>
    </LegalShell>
  );
}

/* -----------------------------------------------------------------------
 * KVKK Aydınlatma Metni (Kısa)
 * --------------------------------------------------------------------- */
export function KVKK() {
  return (
    <LegalShell title="KVKK Aydınlatma Metni" testid="legal-kvkk-page">
      <CompanyBlock />

      <H2>1. Aydınlatma</H2>
      <P>
        6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") kapsamında,
        kişisel verilerinizin hangi amaçla, hangi hukuki sebeple ve nasıl
        işlendiği hakkında sizi bilgilendiririz.
      </P>

      <H2>2. Veri Sorumlusu</H2>
      <P>{COMPANY.name} — {COMPANY.address} — {COMPANY.email}</P>

      <H2>3. İşlenen Veriler ve Amaç</H2>
      <P>
        Detaylı bilgi için <Link to="/gizlilik" className="underline">Gizlilik ve Güvenlik Politikası</Link>'na bakınız.
        Kısaca; hesabın oluşturulması, aboneliğin sunulması, faturalandırma ve
        müşteri destek amacıyla kimlik, iletişim ve ödeme verileriniz işlenir.
      </P>

      <H2>4. Haklarınız</H2>
      <P>
        KVKK m.11 çerçevesinde bilgi talep etme, düzeltme, silme ve itiraz
        haklarına sahipsiniz. Başvurularınızı{" "}
        <a href={`mailto:${COMPANY.email}`} className="underline">{COMPANY.email}</a>
        {" "}adresine iletebilirsiniz.
      </P>
    </LegalShell>
  );
}
